import React, { useMemo } from 'react';
import {
  ResponsiveContainer, LineChart, Line, ReferenceLine,
  XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';
import { calculateXOVR } from '../lib/xovr-engine';
import { calculateOVR } from '../lib/utils';

interface Props {
  games: any[];
  player: any;
}

interface ChartPoint {
  label: string;
  opponent: string;
  xovr: number;
  scoutingOVR: number;
}

const CustomTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  return (
    <div className="bg-canvas border border-ink-mute rounded-xl px-3 py-2 shadow-xl">
      <p className="text-[9px] font-semibold text-ink-2 uppercase tracking-widest mb-0.5">
        {d?.opponent ? `vs ${d.opponent}` : d?.label}
      </p>
      <p className="text-base font-black text-accent-ink">{d?.xovr}</p>
      <p className="text-[9px] text-ink-2">xOVR</p>
    </div>
  );
};

export const XOVRTrendChart: React.FC<Props> = ({ games, player }) => {
  const scoutingOVR = useMemo(
    () => calculateOVR(player?.attributes, player?.primaryPosition),
    [player]
  );

  const { chartData, tooFewGames } = useMemo(() => {
    if (!player || !games?.length) return { chartData: [] as ChartPoint[], tooFewGames: false };

    // Only games that have stats for this player, sorted oldest → newest
    const relevant = [...games]
      .filter(g => g.stats?.[player.id] != null)
      .sort((a, b) => new Date(a.date ?? 0).getTime() - new Date(b.date ?? 0).getTime());

    if (relevant.length < 2) return { chartData: [] as ChartPoint[], tooFewGames: relevant.length === 1 };

    // Accumulate stats game by game and compute xOVR at each point
    let cumPA = 0, cumAB = 0, cumSO = 0;
    let cumH = 0, cumBB = 0, cumHBP = 0, cumTotalBases = 0;
    let cumIP = 0, cumWhipWeighted = 0;
    let cumBF = 0, cumER = 0;

    const points: ChartPoint[] = relevant.map((g, idx) => {
      const s = g.stats[player.id];
      const label    = `G${idx + 1}`;
      const opponent = (g.opponent || '').substring(0, 10);

      // Use frozen snapshot when available (written at import time with the
      // scouting OVR that was current then). Falls back to live computation
      // for games imported before this feature was added.
      if (s.xovrSnapshot != null) {
        return { label, opponent, xovr: s.xovrSnapshot as number, scoutingOVR };
      }

      // Batting accumulation — use counting stats to reconstruct OPS correctly
      const pa  = parseFloat(s.pa)  || 0;
      const ab  = parseFloat(s.ab)  || 0;
      const so  = parseFloat(s.so)  || 0;
      const h   = parseFloat(s.h)   || 0;
      const bb  = parseFloat(s.bb)  || 0;
      const hbp = parseFloat(s.hbp) || 0;
      // slg × ab = total bases for this game
      const slg = s.slg != null ? parseFloat(s.slg) : null;

      if (pa > 0) {
        cumPA += pa;
        cumAB += ab;
        cumSO += so;
        cumH  += h;
        cumBB += bb;
        cumHBP += hbp;
        if (slg != null && ab > 0) cumTotalBases += slg * ab;
      }

      // Pitching accumulation
      const ip   = parseFloat(s.ip)  || 0;
      const whip = s.whip != null ? parseFloat(s.whip) : null;
      const bf   = parseFloat(s.bf) || 0;
      const er   = parseFloat(s.erAllowed) || 0;

      if (ip > 0 && whip != null) {
        cumIP += ip;
        cumWhipWeighted += whip * ip;
        cumBF += bf;
        cumER += er;
      }

      // Build cumulative stats object for calculateXOVR
      const cumulativeStats: any = {};

      if (cumPA > 0) {
        cumulativeStats.pa = cumPA;
        cumulativeStats.ab = cumAB;
        cumulativeStats.so = cumSO;
        // Reconstruct OPS from counting stats (same method as season aggregate)
        const obpDenom = cumAB + cumBB + cumHBP;
        const cumOBP = obpDenom > 0 ? (cumH + cumBB + cumHBP) / obpDenom : 0;
        const cumSLG = cumAB > 0 ? cumTotalBases / cumAB : 0;
        if (obpDenom > 0) cumulativeStats.ops = cumOBP + cumSLG;
      }

      if (cumIP > 0) {
        cumulativeStats.ip   = cumIP;
        cumulativeStats.whip = cumWhipWeighted / cumIP;
        if (cumBF > 0 && cumIP >= 1) {
          cumulativeStats.era = (cumER * 3) / cumIP;
        }
      }

      const breakdown = calculateXOVR(scoutingOVR, Object.keys(cumulativeStats).length > 0 ? cumulativeStats : null);

      return { label, opponent, xovr: breakdown.xovr, scoutingOVR };
    });

    return { chartData: points, tooFewGames: false };
  }, [games, player, scoutingOVR]);

  const isEmpty = chartData.length < 2;

  const yValues = isEmpty ? [] : chartData.map((d: any) => d.xovr);
  const yMin = isEmpty ? scoutingOVR - 5 : Math.max(1,  Math.min(...yValues, scoutingOVR) - 5);
  const yMax = isEmpty ? scoutingOVR + 5 : Math.min(100, Math.max(...yValues, scoutingOVR) + 5);

  return (
    <div className="bg-canvas rounded-3xl border border-hairline p-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-ink-2">xOVR Trendline</p>
        {!isEmpty && (
          <span className="text-[9px] font-semibold text-ink-2 uppercase tracking-widest">
            Season progression
          </span>
        )}
      </div>

      {isEmpty ? (
        <div className="h-[140px] flex items-center justify-center">
          <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest text-center leading-relaxed">
            {tooFewGames
              ? 'Play 2+ games to see your trendline'
              : 'No game stats yet'}
          </p>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={160}>
          <LineChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 9, fill: '#52525b', fontWeight: 700 }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              domain={[yMin, yMax]}
              tick={{ fontSize: 9, fill: '#52525b', fontWeight: 700 }}
              axisLine={false}
              tickLine={false}
              width={28}
            />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine
              y={scoutingOVR}
              stroke="#52525b"
              strokeDasharray="4 3"
              strokeWidth={1}
              label={{ value: `Scout ${scoutingOVR}`, position: 'insideTopRight', fontSize: 8, fill: '#52525b' }}
            />
            <Line
              type="monotone"
              dataKey="xovr"
              stroke="#10b981"
              strokeWidth={2}
              dot={{ fill: '#10b981', r: 3, strokeWidth: 0 }}
              activeDot={{ r: 5, fill: '#10b981', strokeWidth: 0 }}
              connectNulls={false}
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};
